import "./style.css";
import { ApiError, artifactUrl, discoverBackends, flushMutations, getProject, mutate, pairMac, request } from "./api";
import { cloudSession, loginPasskey, registerPasskey } from "./auth";
import { db } from "./db";
import { resumeUpload, uploadVideo } from "./upload";
import type { Backend, BackendKind, Project, Segment, Word } from "./types";

const root = document.querySelector<HTMLDivElement>("#app")!;
let backends: Record<BackendKind, Backend>;
let backend: Backend;
let project: Project | null = null;
let screen = "boot";
let notice = "";
let progress = 0;
let progressLabel = "";
let previewUrl = "";
let resultUrl = "";
let resultFile: File | null = null;
let resultPreparing = false;
let resultDownloadProgress = 0;
let selectedWordId = "";
let pollTimer = 0;
let saveTimer = 0;
let settingsOpen = false;
let cloudAuthenticated = false;
let pendingBootstrap = sessionStorage.getItem("titulkyBootstrap") || "";
const AUTO_PREPARE_RESULT_BYTES = 200 * 1e6;

const stateLabels: Record<string, [string, string]> = {
  uploading: ["Video čeká na nahrání", "Nezavírej aplikaci, dokud není celé na serveru."],
  ingesting: ["Připravuju zvuk a náhled", "Mac nebo cloud kontroluje video."],
  transcribing: ["Poslouchám češtinu", "Teď vzniká první návrh vět."],
  aligning: ["Srovnávám každé slovo", "Akustický model hledá přesný začátek a konec."],
  rendering: ["Vypaluju hotové titulky", "H.264, AAC a stejná velikost i tempo videa."],
};

function escape(value: unknown): string {
  return String(value ?? "").replace(/[&<>"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[char]!);
}

function icon(name: "back" | "settings" | "play" | "check" | "upload" | "cloud" | "mac" | "yellow" | "hide" | "edit" | "share"): string {
  const paths = {
    back: '<path d="m15 18-6-6 6-6"/>',
    settings: '<path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.09A1.7 1.7 0 0 0 8.5 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3v-4h.09A1.7 1.7 0 0 0 4.6 8.5a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3h4v.09A1.7 1.7 0 0 0 15.5 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.14.37.35.7.6 1 .3.3.7.4 1.1.4h.09v4h-.09a1.7 1.7 0 0 0-1.7.6Z"/>',
    play: '<path d="m9 7 8 5-8 5V7Z"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    upload: '<path d="M12 16V4m0 0L7 9m5-5 5 5M5 20h14"/>',
    cloud: '<path d="M7 18h11a4 4 0 0 0 .4-8 6 6 0 0 0-11.5-1.5A4.8 4.8 0 0 0 7 18Z"/>',
    mac: '<rect x="4" y="4" width="16" height="12" rx="2"/><path d="M8 20h8m-6-4-.5 4m4.5-4 .5 4"/>',
    yellow: '<circle cx="12" cy="12" r="7"/>',
    hide: '<path d="M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.9 4.2A10.8 10.8 0 0 1 21 12a11.8 11.8 0 0 1-3 4.2M6.6 6.6A11.5 11.5 0 0 0 3 12s3.3 6 9 6c.8 0 1.6-.1 2.3-.4"/>',
    edit: '<path d="m4 20 4.5-1L19 8.5 15.5 5 5 15.5 4 20Z"/><path d="m13.5 7 3.5 3.5"/>',
    share: '<path d="M12 16V4m0 0L8 8m4-4 4 4M5 12v8h14v-8"/>',
  };
  return `<svg viewBox="0 0 24 24" aria-hidden="true">${paths[name]}</svg>`;
}

function header(title: string, back = false): string {
  return `<header class="topbar">
    ${back ? `<button class="icon-button" data-action="home" aria-label="Zpět">${icon("back")}</button>` : '<div class="brand-mark">G</div>'}
    <div><div class="eyebrow">GAVIAL</div><h1>${escape(title)}</h1></div>
    <button class="icon-button topbar-settings" data-action="settings" aria-label="Nastavení">${icon("settings")}</button>
  </header>`;
}

function backendBadge(): string {
  const healthy = backend.available;
  return `<div class="backend-badge"><i class="status-dot ${healthy ? "online" : ""}"></i>${backend.kind === "mac" ? "Mac přes Tailscale" : "GAVIAL Cloud"}</div>`;
}

function render(): void {
  clearTimeout(pollTimer);
  if (screen === "boot") root.innerHTML = `<main class="center-screen"><div class="pulse-logo">G</div><p>Probouzím titulky…</p></main>`;
  else if (screen === "home") renderHome();
  else if (screen === "upload") renderUpload();
  else if (screen === "resume") renderResume();
  else if (screen === "auth") renderAuth();
  else if (screen === "transcript") renderTranscript();
  else if (screen === "words") renderWords();
  else if (screen === "ready") renderReady();
  else if (screen === "failed") renderFailed();
  else renderProcessing();
  if (settingsOpen) renderSettings();
  bindCommon();
}

function renderHome(): void {
  const selected = backend.kind;
  root.innerHTML = `<main class="app-shell home-shell">
    ${header("Titulky")}
    <section class="hero">
      <span class="hero-kicker">SLOVO PO SLOVĚ</span>
      <h2>Hotové titulky.<br><em>Bez timeline.</em></h2>
      <p>Vyber už sestříhané video. Češtinu opravíš tady, časování uděláme za tebe.</p>
    </section>
    ${notice ? `<div class="notice">${escape(notice)}</div>` : ""}
    <section class="capacity-grid" aria-label="Místo zpracování">
      <button class="capacity-card ${selected === "mac" ? "selected" : ""} ${!backends.mac.available ? "unavailable" : ""}" data-backend="mac">
        <span class="capacity-icon">${icon("mac")}</span><span><strong>Mac</strong><small>${backends.mac.available ? "Je připraven" : "Teď není dostupný"}</small></span><i></i>
      </button>
      <button class="capacity-card ${selected === "cloud" ? "selected" : ""} ${!backends.cloud.available ? "unavailable" : ""}" data-backend="cloud">
        <span class="capacity-icon">${icon("cloud")}</span><span><strong>Cloud</strong><small>${backends.cloud.available ? "Je připraven" : "Čeká na nasazení"}</small></span><i></i>
      </button>
    </section>
    <label class="upload-cta ${!backend.available ? "disabled" : ""}">${icon("upload")}<span>Vybrat video z Fotek<small>MP4 nebo MOV · nejvýše 15 min / 4 GB</small></span><input id="video-input" type="file" accept="video/mp4,video/quicktime,video/*" ${!backend.available ? "disabled" : ""}></label>
    <p class="privacy-note">Soubor i přepis se automaticky smažou za 3 dny.</p>
  </main>`;
  root.querySelectorAll<HTMLElement>("[data-backend]").forEach((button) => button.addEventListener("click", () => {
    const selectedBackend = backends[button.dataset.backend as BackendKind];
    backend = selectedBackend;
    localStorage.setItem("preferredBackend", backend.kind);
    render();
  }));
  root.querySelector<HTMLInputElement>("#video-input")?.addEventListener("change", (event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) void beginUpload(file);
  });
}

function renderUpload(): void {
  root.innerHTML = `<main class="app-shell">${header("Nahrávání", true)}<section class="center-card">
    <div class="progress-orbit" style="--progress:${Math.max(0.02, progress)}"><span>${Math.round(progress * 100)}<small>%</small></span></div>
    <h2>Posílám video</h2><p>${escape(progressLabel)}</p>${backendBadge()}
    <p class="stay-open">Po uspání Safari bude upload pokračovat od poslední hotové části.</p>
  </section></main>`;
}

function renderResume(): void {
  root.innerHTML = `<main class="app-shell">${header("Pokračovat", true)}<section class="center-card resume-card">
    <div class="resume-icon">${icon("upload")}</div><span class="hero-kicker">UPLOAD JE ULOŽENÝ</span><h2>Vyber stejné video.</h2><p>Hotové části už znovu neposílám. iPhone potřebuje jen znovu povolit přístup k souboru ve Fotkách.</p>
    <label class="upload-cta">${icon("upload")}<span>Vybrat stejné video<small>${escape(project?.media.original_name || "")}</small></span><input id="resume-input" type="file" accept="video/*"></label>
  </section></main>`;
  root.querySelector<HTMLInputElement>("#resume-input")?.addEventListener("change", (event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) void continueUpload(file);
  });
}

function renderProcessing(): void {
  const [title, detail] = stateLabels[project?.state || ""] || ["Pracuju na videu", "Stav se za chvíli obnoví."];
  root.innerHTML = `<main class="app-shell">${header("Zpracování", true)}<section class="center-card processing-card">
    <div class="sound-wave"><i></i><i></i><i></i><i></i><i></i><i></i><i></i></div>
    <span class="step-pill">${escape(project?.state || "")}</span><h2>${title}</h2><p>${detail}</p>${backendBadge()}
    <div class="workflow"><span class="done">Video</span><b></b><span class="${project?.state === "transcribing" ? "active" : ""}">Text</span><b></b><span class="${project?.state === "aligning" ? "active" : ""}">Časy</span><b></b><span class="${project?.state === "rendering" ? "active" : ""}">MP4</span></div>
  </section></main>`;
  pollTimer = window.setTimeout(() => void refreshProject(), 1800);
}

function renderAuth(): void {
  if (pendingBootstrap) {
    root.innerHTML = `<main class="app-shell">${header("První spuštění", true)}<section class="auth-card">
      <div class="face-symbol"><span></span><span></span><span></span><span></span></div>
      <span class="hero-kicker">POSLEDNÍ KROK</span><h2>Mac je připravený.<br>Zapni i cloud.</h2><p>Kód už je bezpečně načtený z QR. Potvrď jen uložení soukromého passkey přes Face ID.</p>
      <input id="bootstrap" type="hidden" value="${escape(pendingBootstrap)}">
      <button class="primary-button" data-action="register">Aktivovat přes Face ID</button>
      <small>Po aktivaci funguje Mac automaticky a cloud ho zastoupí, když nebude dostupný.</small>
    </section></main>`;
    return;
  }
  root.innerHTML = `<main class="app-shell">${header("Face ID", true)}<section class="auth-card">
    <div class="face-symbol"><span></span><span></span><span></span><span></span></div>
    <span class="hero-kicker">SOUKROMÝ CLOUD</span><h2>Jen tvoje titulky.</h2><p>Cloud nemá veřejnou registraci. Přihlas se passkey uloženým v iPhonu.</p>
    <button class="primary-button" data-action="login">Přihlásit přes Face ID</button>
    <details><summary>První spuštění nebo recovery</summary><label class="field"><span>Bootstrap kód z Macu</span><input id="bootstrap" inputmode="text" autocomplete="one-time-code" placeholder="XXXX-XXXX"></label><button class="secondary-button" data-action="register">Aktivovat tento iPhone</button></details>
  </section></main>`;
}

function videoStage(showCaption = false): string {
  const style = project!.project_style;
  return `<div class="video-stage" style="--caption-bottom:${style.spodni_okraj_pct}%;--caption-color:${style.barva_bila};--caption-yellow:${style.barva_zluta}">
    <video id="preview-video" playsinline preload="metadata" ${previewUrl ? `src="${escape(previewUrl)}"` : ""} controls></video>
    ${showCaption ? '<div id="caption-preview" class="caption-preview" aria-live="off"></div>' : ""}
  </div>`;
}

function renderTranscript(): void {
  root.innerHTML = `<main class="app-shell editor-shell">${header("Oprava vět", true)}${videoStage()}
    <section class="section-heading"><div><span class="hero-kicker">1 / 2</span><h2>Co přesně zaznělo?</h2></div><span>${project!.segmenty.length} vět</span></section>
    <section class="sentence-list">${project!.segmenty.map((segment, index) => `<article class="sentence-card ${segment.nezaznelo ? "muted" : ""}" data-segment="${segment.id}">
      <button class="sentence-play" data-play-segment="${segment.id}" aria-label="Přehrát větu">${icon("play")}</button>
      <label><span>Věta ${index + 1}</span><textarea rows="2" data-segment-text="${segment.id}" ${segment.nezaznelo ? "disabled" : ""}>${escape(segment.text)}</textarea></label>
      <button class="not-spoken ${segment.nezaznelo ? "active" : ""}" data-not-spoken="${segment.id}">${icon("check")} Nezaznělo</button>
    </article>`).join("")}</section>
    <div class="sticky-action"><span id="save-state">Uloženo</span><button class="primary-button" data-action="align">Text sedí · přesně zarovnat</button></div>
  </main>`;
  void ensurePreview();
  bindTranscript();
}

function renderWords(): void {
  const selected = project!.slova.find((word) => word.id === selectedWordId) || project!.slova.find((word) => !word.skryte) || project!.slova[0];
  if (selected) selectedWordId = selected.id;
  root.innerHTML = `<main class="app-shell word-shell">${header("Slova", true)}${videoStage(true)}
    <section class="word-toolbar">
      <button class="tool-button ${selected?.zlute ? "yellow-active" : ""}" data-word-action="yellow">${icon("yellow")}<span>${selected?.zlute ? "Žluté" : "Bílé"}</span></button>
      <button class="tool-button ${selected?.skryte ? "active" : ""}" data-word-action="hide">${icon("hide")}<span>${selected?.skryte ? "Skryté" : "Skrýt"}</span></button>
      <button class="tool-button" data-word-action="edit">${icon("edit")}<span>Přepsat</span></button>
    </section>
    <section class="timing-tools" aria-label="Doladění času slova">
      <span>Start</span><button data-time-action="start-back">−20</button><button data-time-action="start-forward">+20</button>
      <i></i><span>Konec</span><button data-time-action="end-back">−20</button><button data-time-action="end-forward">+20</button><small>ms</small>
    </section>
    <div class="caption-rail" id="caption-rail">${project!.slova.map((word) => `<button data-word="${word.id}" class="rail-word ${word.id === selectedWordId ? "selected" : ""} ${word.zlute ? "yellow" : ""} ${word.skryte ? "hidden-word" : ""}"><span>${escape(word.text)}</span><small>${formatTime(word.start)}</small></button>`).join("")}</div>
    <section class="style-panel"><div class="section-heading"><div><span class="hero-kicker">VZHLED VIDEA</span><h2>Velikost a výška</h2></div></div>
      <label class="range-row"><span>Velikost <output id="size-output">${project!.project_style.velikost_pct.toFixed(1)} %</output></span><input id="style-size" type="range" min="3.5" max="11" step="0.1" value="${project!.project_style.velikost_pct}"></label>
      <label class="range-row"><span>Odspodu <output id="bottom-output">${project!.project_style.spodni_okraj_pct.toFixed(1)} %</output></span><input id="style-bottom" type="range" min="3" max="35" step="0.5" value="${project!.project_style.spodni_okraj_pct}"></label>
    </section>
    <div class="sticky-action"><span id="save-state">Uloženo</span><button class="primary-button" data-action="render">Vyrenderovat MP4</button></div>
  </main>`;
  void ensurePreview(true);
  bindWordEditor();
}

function renderReady(): void {
  const size = project!.artifacts.result?.size || 0;
  const sizeMb = size ? `${(size / 1e6).toFixed(0)} MB` : "video";
  const shareLabel = resultPreparing ? `Připravuju ${resultDownloadProgress} %` : resultFile ? "Otevřít sdílení" : "Připravit video";
  const shareHint = resultPreparing
    ? `Stahuju ${sizeMb}. Nech aplikaci otevřenou.`
    : resultFile
      ? "Klepni a ve sdílení iPhonu zvol Uložit video."
      : `První klepnutí připraví ${sizeMb}. Druhé otevře sdílení iPhonu.`;
  root.innerHTML = `<main class="app-shell">${header("Hotovo", true)}<section class="result-hero">
    <div class="success-mark">${icon("check")}</div><span class="hero-kicker">READY TO POST</span><h2>Titulky jsou ve videu.</h2><p>${Math.round(project!.trvani)} s · ${project!.sirka}×${project!.vyska} · ${project!.fps} FPS</p>
  </section>
  <div class="result-video"><video id="result-video" playsinline controls ${resultUrl ? `src="${escape(resultUrl)}"` : ""}></video></div>
  <section class="result-actions"><button class="primary-button share-button" data-action="share" ${resultPreparing ? "disabled" : ""}>${icon("share")} <span>${shareLabel}</span></button><p class="result-save-status" id="result-save-status" aria-live="polite">${shareHint}</p><button class="secondary-button" data-action="open-result">Nouzově otevřít video</button>
    <div class="file-links"><button data-artifact="srt">SRT</button><button data-artifact="ass">ASS</button><span>${size ? `${(size / 1e6).toFixed(0)} MB` : "MP4"}</span></div>
  </section><button class="new-project" data-action="new">+ Další video</button></main>`;
  void ensureResult();
  if (size > 0 && size <= AUTO_PREPARE_RESULT_BYTES && !resultFile && !resultPreparing) void prepareResultFile();
}

function renderFailed(): void {
  root.innerHTML = `<main class="app-shell">${header("Něco se zaseklo", true)}<section class="error-card"><span>!</span><h2>Tohle nedoběhlo.</h2><p>${escape(project?.error || "Server vrátil chybu bez detailu.")}</p><button class="primary-button" data-action="home">Zpátky na začátek</button></section></main>`;
}

function renderSettings(): void {
  root.insertAdjacentHTML("beforeend", `<div class="sheet-backdrop" data-action="close-settings"><section class="settings-sheet" role="dialog" aria-modal="true"><i class="sheet-handle"></i><h2>Připojení</h2>
    <label class="field"><span>Tailscale adresa Macu</span><input id="mac-url" type="url" value="${escape(localStorage.getItem("macUrl") || "")}" placeholder="https://mac.tailnet.ts.net"></label>
    <label class="field"><span>Token zařízení</span><input id="mac-token" type="password" value="${escape(localStorage.getItem("macToken") || "")}" placeholder="Vloží se automaticky přes QR"></label>
    <button class="primary-button" data-action="save-settings">Uložit připojení</button><button class="sheet-close" data-action="close-settings">Zavřít</button>
    ${project ? '<button class="danger-button" data-action="delete-project">Smazat tento projekt hned</button>' : ""}
    <p>Impact je privátní a posílá ho až přihlášený renderer. Ve veřejné aplikaci není uložený.</p>
  </section></div>`);
}

function bindCommon(): void {
  root.querySelector<HTMLElement>(".settings-sheet")?.addEventListener("click", (event) => event.stopPropagation());
  root.querySelectorAll<HTMLElement>("[data-action]").forEach((element) => element.addEventListener("click", (event) => {
    const action = element.dataset.action;
    if (action === "settings") { settingsOpen = true; render(); }
    if (action === "close-settings") { settingsOpen = false; render(); }
    if (action === "save-settings") void saveSettings();
    if (action === "home") { screen = project ? routeForProject(project) : "home"; if (screen !== "home") { project = null; screen = "home"; } render(); }
    if (action === "align") void startAlignment();
    if (action === "render") void startRender();
    if (action === "login") void authenticateCloud(false);
    if (action === "register") void authenticateCloud(true);
    if (action === "share") void shareResult();
    if (action === "open-result") void openResult();
    if (action === "new") void newProject();
    if (action === "delete-project") void deleteProjectNow();
    event.stopPropagation();
  }));
  root.querySelectorAll<HTMLElement>("[data-artifact]").forEach((button) => button.addEventListener("click", () => void openArtifact(button.dataset.artifact!)));
}

function bindTranscript(): void {
  root.querySelectorAll<HTMLTextAreaElement>("[data-segment-text]").forEach((input) => input.addEventListener("input", () => {
    const segment = project!.segmenty.find((item) => item.id === input.dataset.segmentText);
    if (segment) segment.text = input.value;
    scheduleTranscriptSave();
  }));
  root.querySelectorAll<HTMLElement>("[data-not-spoken]").forEach((button) => button.addEventListener("click", () => {
    const segment = project!.segmenty.find((item) => item.id === button.dataset.notSpoken);
    if (segment) segment.nezaznelo = !segment.nezaznelo;
    render();
    scheduleTranscriptSave(50);
  }));
  root.querySelectorAll<HTMLElement>("[data-play-segment]").forEach((button) => button.addEventListener("click", () => {
    const segment = project!.segmenty.find((item) => item.id === button.dataset.playSegment);
    if (segment) playRange(segment.start, segment.konec);
  }));
}

function bindWordEditor(): void {
  const video = root.querySelector<HTMLVideoElement>("#preview-video");
  video?.addEventListener("timeupdate", updateCaption);
  video?.addEventListener("loadedmetadata", updateCaptionSize);
  root.querySelectorAll<HTMLElement>("[data-word]").forEach((button) => button.addEventListener("click", () => {
    selectedWordId = button.dataset.word!;
    const word = selectedWord();
    if (video && word) video.currentTime = Math.max(0, word.start - 0.02);
    render();
  }));
  root.querySelectorAll<HTMLElement>("[data-word-action]").forEach((button) => button.addEventListener("click", () => void wordAction(button.dataset.wordAction!)));
  root.querySelectorAll<HTMLElement>("[data-time-action]").forEach((button) => button.addEventListener("click", () => void timeAction(button.dataset.timeAction!)));
  const size = root.querySelector<HTMLInputElement>("#style-size");
  const bottom = root.querySelector<HTMLInputElement>("#style-bottom");
  size?.addEventListener("input", () => { project!.project_style.velikost_pct = Number(size.value); root.querySelector("#size-output")!.textContent = `${Number(size.value).toFixed(1)} %`; updateCaptionSize(); scheduleStyleSave(); });
  bottom?.addEventListener("input", () => { project!.project_style.spodni_okraj_pct = Number(bottom.value); root.querySelector<HTMLElement>(".video-stage")?.style.setProperty("--caption-bottom", `${bottom.value}%`); root.querySelector("#bottom-output")!.textContent = `${Number(bottom.value).toFixed(1)} %`; scheduleStyleSave(); });
  updateCaption();
}

function selectedWord(): Word | undefined { return project?.slova.find((word) => word.id === selectedWordId); }

async function beginUpload(file: File): Promise<void> {
  if (backend.kind === "cloud" && !cloudAuthenticated) {
    cloudAuthenticated = await cloudSession();
    if (!cloudAuthenticated) { screen = "auth"; notice = "Po přihlášení vyber video znovu."; render(); return; }
  }
  screen = "upload"; progress = 0; progressLabel = "Připravuju bezpečný upload…"; render();
  try {
    project = await uploadVideo(backend, file, (value, label) => { progress = value; progressLabel = label; render(); });
    route();
  } catch (error) { showError(error); }
}

async function continueUpload(file: File): Promise<void> {
  if (!project) return;
  const record = await db.getUpload(`${backend.kind}:${project.project_id}`);
  if (!record) {
    notice = "Rozpracovaný upload už vypršel. Začni novým projektem.";
    project = null; screen = "home"; render(); return;
  }
  screen = "upload";
  progress = record.receivedParts.length / Math.max(1, Math.ceil(record.size / record.chunkSize));
  progressLabel = "Navazuju na poslední hotovou část…";
  render();
  try {
    project = await resumeUpload(backend, file, project, record, (value, label) => {
      progress = value; progressLabel = label; render();
    });
    route();
  } catch (error) { showError(error); }
}

async function refreshProject(): Promise<void> {
  if (!project) return;
  try { project = await getProject(backend, project.project_id); route(); } catch (error) { showError(error, false); pollTimer = window.setTimeout(() => void refreshProject(), 3000); }
}

function routeForProject(value: Project): string {
  if (value.state === "uploading") return "resume";
  if (value.state === "transcript_review") return "transcript";
  if (value.state === "word_edit") return "words";
  if (value.state === "ready") return "ready";
  if (value.state === "failed") return "failed";
  return "processing";
}

function route(): void { if (project) { screen = routeForProject(project); render(); } }

async function ensurePreview(withFont = false): Promise<void> {
  if (!project) return;
  try {
    if (!previewUrl) previewUrl = await artifactUrl(backend, project.project_id, "preview");
    const video = root.querySelector<HTMLVideoElement>("#preview-video");
    if (video && !video.src) video.src = previewUrl;
    if (withFont) {
      const fontUrl = await artifactUrl(backend, project.project_id, "font");
      const style = document.createElement("style");
      style.textContent = `@font-face{font-family:GavialImpact;src:url('${fontUrl}') format('truetype');font-display:swap}`;
      document.head.append(style);
    }
  } catch (error) { showError(error, false); }
}

async function ensureResult(): Promise<void> {
  if (!project) return;
  try { const url = await freshResultUrl(); const video = root.querySelector<HTMLVideoElement>("#result-video"); if (video) video.src = url; } catch (error) { showError(error, false); }
}

async function freshResultUrl(): Promise<string> {
  if (!project) throw new Error("Hotové video už není otevřené.");
  resultUrl = await artifactUrl(backend, project.project_id, "result");
  return resultUrl;
}

function playRange(start: number, end: number): void {
  const video = root.querySelector<HTMLVideoElement>("#preview-video");
  if (!video) return;
  video.currentTime = Math.max(0, start - 0.45);
  void video.play();
  const stop = () => { if (video.currentTime >= end + 0.45) { video.pause(); video.removeEventListener("timeupdate", stop); } };
  video.addEventListener("timeupdate", stop);
}

function scheduleTranscriptSave(delay = 700): void {
  const state = root.querySelector("#save-state"); if (state) state.textContent = "Ukládám…";
  clearTimeout(saveTimer); saveTimer = window.setTimeout(() => void saveTranscript(), delay);
}

async function saveTranscript(): Promise<void> {
  if (!project) return;
  const response = await mutate<Project>(backend, `/api/v1/projects/${project.project_id}/transcript`, "PATCH", { revision: project.revision, segments: project.segmenty.map(({ id, text, nezaznelo }) => ({ id, text, nezaznelo })) });
  if (response) project = response;
  const state = root.querySelector("#save-state"); if (state) state.textContent = response ? "Uloženo" : "Uložím po připojení";
}

async function startAlignment(): Promise<void> {
  clearTimeout(saveTimer); await saveTranscript(); if (!project) return;
  try { project = await request<Project>(backend, `/api/v1/projects/${project.project_id}/align`, { method: "POST", body: JSON.stringify({ revision: project.revision, idempotency_key: `align:${project.project_id}:${project.revision}` }) }); route(); } catch (error) { showError(error); }
}

async function wordAction(action: string): Promise<void> {
  const word = selectedWord(); if (!word || !project) return;
  if (action === "yellow") word.zlute = !word.zlute;
  if (action === "hide") word.skryte = !word.skryte;
  if (action === "edit") { const value = window.prompt("Jak má slovo vypadat?", word.text); if (value === null || !value.trim()) return; word.text = value.trim(); }
  const response = await mutate<Project>(backend, `/api/v1/projects/${project.project_id}/words`, "PATCH", { revision: project.revision, words: project.slova.map(({ id, text, zlute, skryte, start, konec }) => ({ id, text, zlute, skryte, start, konec })) });
  if (response) project = response;
  render();
}

async function timeAction(action: string): Promise<void> {
  const word = selectedWord();
  if (!word) return;
  const delta = action.endsWith("forward") ? .02 : -.02;
  if (action.startsWith("start")) word.start = Math.max(0, Math.min(word.konec - .01, word.start + delta));
  else word.konec = Math.max(word.start + .01, word.konec + delta);
  await saveAllWords();
}

async function saveAllWords(): Promise<void> {
  if (!project) return;
  const response = await mutate<Project>(backend, `/api/v1/projects/${project.project_id}/words`, "PATCH", {
    revision: project.revision,
    words: project.slova.map(({ id, text, zlute, skryte, start, konec }) => ({ id, text, zlute, skryte, start, konec })),
  });
  if (response) project = response;
  render();
}

function scheduleStyleSave(): void { const state = root.querySelector("#save-state"); if (state) state.textContent = "Ukládám…"; clearTimeout(saveTimer); saveTimer = window.setTimeout(() => void saveStyle(), 500); }
async function saveStyle(): Promise<void> { if (!project) return; const style = project.project_style; const response = await mutate<Project>(backend, `/api/v1/projects/${project.project_id}/style`, "PATCH", { revision: project.revision, velikost_pct: style.velikost_pct, spodni_okraj_pct: style.spodni_okraj_pct }); if (response) project = response; const state = root.querySelector("#save-state"); if (state) state.textContent = response ? "Uloženo" : "Uložím po připojení"; }

async function startRender(): Promise<void> { clearTimeout(saveTimer); await saveStyle(); if (!project) return; try { project = await request<Project>(backend, `/api/v1/projects/${project.project_id}/render`, { method: "POST", body: JSON.stringify({ revision: project.revision, idempotency_key: `render:${project.project_id}:${project.revision}` }) }); route(); } catch (error) { showError(error); } }

function updateCaption(): void {
  const video = root.querySelector<HTMLVideoElement>("#preview-video"); const caption = root.querySelector<HTMLElement>("#caption-preview"); if (!video || !caption || !project) return;
  const time = video.currentTime;
  const visible = project.slova.filter((word) => !word.skryte);
  const word = visible.find((item, index) => {
    const start = item.zobrazeni_start ?? Math.max(0, item.start + project!.posun_ms / 1000 - project!.project_style.naskok_ms / 1000);
    const fallbackEnd = Math.min(item.konec + project!.posun_ms / 1000 + project!.project_style.drzeni_ms / 1000, visible[index + 1]?.start ?? Infinity);
    const end = item.zobrazeni_konec ?? fallbackEnd;
    return time >= start && time < end;
  });
  caption.textContent = word ? (project.project_style.verzalky ? word.text.toUpperCase().replace(/[,.;:]$/, "") : word.text) : "";
  caption.classList.toggle("yellow", Boolean(word?.zlute));
  if (word && word.id !== selectedWordId && !video.paused) { selectedWordId = word.id; root.querySelector(`[data-word="${word.id}"]`)?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", inline: "center", block: "nearest" }); root.querySelectorAll(".rail-word.selected").forEach((item) => item.classList.remove("selected")); root.querySelector(`[data-word="${word.id}"]`)?.classList.add("selected"); }
  updateCaptionSize();
}

function updateCaptionSize(): void { const stage = root.querySelector<HTMLElement>(".video-stage"); const caption = root.querySelector<HTMLElement>("#caption-preview"); if (stage && caption && project) caption.style.fontSize = `${stage.clientHeight * project.project_style.velikost_pct / 100}px`; }

async function shareResult(): Promise<void> {
  if (!project || resultPreparing) return;
  if (resultFile) { await sharePreparedResult(); return; }
  await prepareResultFile();
}

async function prepareResultFile(): Promise<void> {
  if (!project || resultPreparing || resultFile) return;
  resultPreparing = true;
  resultDownloadProgress = 0;
  updateResultShareUi();
  try {
    const url = await freshResultUrl();
    const response = await fetch(url);
    if (!response.ok) throw new Error("Video se nepodařilo stáhnout. Zkus to prosím znovu.");
    const expectedSize = Number(response.headers.get("Content-Length")) || project.artifacts.result?.size || 0;
    const parts: BlobPart[] = [];
    let received = 0;
    if (response.body) {
      const reader = response.body.getReader();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        parts.push(value);
        received += value.byteLength;
        const nextProgress = expectedSize ? Math.min(99, Math.floor(received / expectedSize * 100)) : resultDownloadProgress;
        if (nextProgress !== resultDownloadProgress) { resultDownloadProgress = nextProgress; updateResultShareUi(); }
      }
    } else {
      parts.push(await response.blob());
    }
    const blob = new Blob(parts, { type: response.headers.get("Content-Type") || "video/mp4" });
    if (!blob.size) throw new Error("Stažené video je prázdné. Zkus to prosím znovu.");
    resultFile = new File([blob], "video-s-titulky.mp4", { type: "video/mp4" });
    resultDownloadProgress = 100;
    resultPreparing = false;
    updateResultShareUi();
    notice = "Video je připravené. Klepni na Otevřít sdílení a zvol Uložit video.";
  } catch (error) {
    resultPreparing = false;
    resultDownloadProgress = 0;
    updateResultShareUi();
    showError(error, false);
  }
}

async function sharePreparedResult(): Promise<void> {
  if (!resultFile) return;
  const shareData = { files: [resultFile], title: "Video s titulky" };
  if (!navigator.share || (navigator.canShare && !navigator.canShare(shareData))) { await openResult(); return; }
  try {
    const operation = navigator.share(shareData);
    if (operation && typeof operation.then === "function") await operation;
    const status = root.querySelector<HTMLElement>("#result-save-status");
    if (status) status.textContent = "Pokud jsi zvolil Uložit video, najdeš ho teď ve Fotkách.";
  } catch (error) {
    if ((error as DOMException).name === "AbortError") return;
    notice = "iPhone sdílení odmítl. Použij Nouzově otevřít video → Sdílet → Uložit video.";
    showError(new Error(notice), false);
  }
}

function updateResultShareUi(): void {
  const button = root.querySelector<HTMLButtonElement>('[data-action="share"]');
  const label = button?.querySelector("span");
  const status = root.querySelector<HTMLElement>("#result-save-status");
  const size = project?.artifacts.result?.size || 0;
  const sizeMb = size ? `${(size / 1e6).toFixed(0)} MB` : "video";
  if (button) button.disabled = resultPreparing;
  if (label) label.textContent = resultPreparing ? `Připravuju ${resultDownloadProgress} %` : resultFile ? "Otevřít sdílení" : "Připravit video";
  if (status) status.textContent = resultPreparing ? `Stahuju ${sizeMb}. Nech aplikaci otevřenou.` : resultFile ? "Klepni a ve sdílení iPhonu zvol Uložit video." : `První klepnutí připraví ${sizeMb}. Druhé otevře sdílení iPhonu.`;
}

async function openResult(): Promise<void> {
  if (!project) return;
  const player = window.open("", "_blank");
  try {
    const url = await freshResultUrl();
    if (player) player.location.href = url;
    else window.location.href = url;
    notice = "V přehrávači klepni Sdílet → Uložit video. Když volba chybí, zvol Uložit do Souborů.";
  } catch (error) {
    player?.close();
    showError(error, false);
  }
}

async function openArtifact(kind: string): Promise<void> { if (!project) return; try { window.open(await artifactUrl(backend, project.project_id, kind), "_blank"); } catch (error) { showError(error); } }

async function saveSettings(): Promise<void> { const url = root.querySelector<HTMLInputElement>("#mac-url")?.value.trim().replace(/\/$/, "") || ""; const token = root.querySelector<HTMLInputElement>("#mac-token")?.value.trim() || ""; localStorage.setItem("macUrl", url); localStorage.setItem("macToken", token); settingsOpen = false; await boot(false); }

async function authenticateCloud(register: boolean): Promise<void> { try { if (register) { const code = root.querySelector<HTMLInputElement>("#bootstrap")?.value.trim(); if (!code) throw new Error("Vlož bootstrap kód z Macu."); await registerPasskey(code); pendingBootstrap = ""; sessionStorage.removeItem("titulkyBootstrap"); } else await loginPasskey(); cloudAuthenticated = true; screen = "home"; notice = register ? "Hotovo. Mac i cloud jsou připravené." : "Cloud je odemčený. Teď vyber video."; render(); } catch (error) { showError(error, false); } }

async function newProject(): Promise<void> { project = null; previewUrl = ""; resultUrl = ""; resultFile = null; resultPreparing = false; resultDownloadProgress = 0; selectedWordId = ""; await db.deleteState("activeProject"); screen = "home"; render(); }

async function deleteProjectNow(): Promise<void> {
  if (!project || !window.confirm("Smazat zdroj, přepis, náhled i hotové video hned?")) return;
  try {
    await request(backend, `/api/v1/projects/${project.project_id}`, { method: "DELETE", body: JSON.stringify({ revision: project.revision }) });
    settingsOpen = false;
    await newProject();
  } catch (error) { showError(error, false); }
}

function formatTime(seconds: number): string { const minutes = Math.floor(seconds / 60); return `${minutes}:${(seconds % 60).toFixed(1).padStart(4, "0")}`; }

function showError(error: unknown, rerender = true): void { if (error instanceof ApiError && error.status === 401 && backend.kind === "cloud") { screen = "auth"; render(); return; } notice = error instanceof Error ? error.message : "Něco se nepovedlo."; if (rerender) { screen = project ? routeForProject(project) : "home"; render(); } else { const existing = root.querySelector(".notice"); if (existing) existing.textContent = notice; else root.querySelector("main")?.insertAdjacentHTML("afterbegin", `<div class="notice floating">${escape(notice)}</div>`); } }

async function boot(restore = true): Promise<void> {
  screen = "boot"; render();
  const params = new URLSearchParams(location.search);
  const bootstrap = params.get("bootstrap")?.trim() || "";
  if (bootstrap) {
    pendingBootstrap = bootstrap;
    sessionStorage.setItem("titulkyBootstrap", bootstrap);
  }
  if (params.has("mac") || params.has("pair_id") || params.has("code") || params.has("bootstrap")) history.replaceState({}, "", location.pathname);
  if (params.get("mac") && params.get("pair_id") && params.get("code")) {
    try { await pairMac(params.get("mac")!, params.get("pair_id")!, params.get("code")!); notice = "iPhone je spárovaný s Macem."; } catch (error) { notice = error instanceof Error ? error.message : "Párování selhalo."; }
  }
  backends = await discoverBackends();
  const preferred = localStorage.getItem("preferredBackend") as BackendKind | null;
  backend = preferred && backends[preferred].available ? backends[preferred] : backends.mac.available ? backends.mac : backends.cloud;
  cloudAuthenticated = backend.kind === "cloud" ? await cloudSession() : false;
  if (pendingBootstrap) {
    cloudAuthenticated = await cloudSession();
    if (!cloudAuthenticated) { screen = "auth"; render(); return; }
    pendingBootstrap = "";
    sessionStorage.removeItem("titulkyBootstrap");
  }
  if (restore) {
    const active = await db.getState<{ backend: BackendKind; baseUrl: string; projectId: string }>("activeProject");
    if (active) {
      const candidate = backends[active.backend];
      candidate.baseUrl = active.baseUrl;
      backend = candidate;
      try { project = await getProject(backend, active.projectId); route(); return; } catch { await db.deleteState("activeProject"); }
    }
  }
  screen = "home"; render();
}

window.addEventListener("online", () => void flushMutations());
window.addEventListener("revision-conflict", () => { notice = "Projekt se změnil jinde. Načetl jsem novější verzi."; void refreshProject(); });
if ("serviceWorker" in navigator) window.addEventListener("load", () => void navigator.serviceWorker.register("/sw.js"));
void boot();
